import { createPortal } from "react-dom"
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { AlertTriangle, Check, Info, Loader2, X } from "lucide-react"
import { accent } from "../../design/palette"
import { cx } from "../../lib/cx"
import { BrandSpinner } from "../brand/Logo"

export { cx }

/* -------------------------------------------------------------------------- */
/* Button                                                                      */
/* -------------------------------------------------------------------------- */

const BUTTON_VARIANTS = {
	primary:
		"bg-brand-700 text-white shadow-card hover:bg-brand-800 active:bg-brand-900 disabled:bg-brand-700/40",
	secondary:
		"bg-white text-ink-700 ring-1 ring-inset ring-ink-200 shadow-card hover:bg-ink-50 hover:text-ink-900 disabled:text-ink-400",
	ghost: "text-ink-600 hover:bg-ink-100 hover:text-ink-900 disabled:text-ink-300",
	danger:
		"bg-white text-rose-700 ring-1 ring-inset ring-rose-200 hover:bg-rose-50 disabled:text-rose-300",
	subtle: "bg-brand-50 text-brand-800 hover:bg-brand-100 disabled:text-brand-400",
	// For a dark panel. Kept as a variant rather than a className override on
	// `secondary`, because two competing background utilities resolve by
	// stylesheet order, not by the order they are written - which is how a
	// white button ends up with white text on it.
	onDark:
		"bg-white/10 text-white ring-1 ring-inset ring-white/30 hover:bg-white/20 disabled:text-white/50",
	onDarkPrimary:
		"bg-white text-brand-900 shadow-card hover:bg-brand-50 disabled:text-brand-400"
}

const BUTTON_SIZES = {
	sm: "h-8 px-2.5 text-[0.8125rem] gap-1.5",
	md: "h-9 px-3.5 text-sm gap-2",
	lg: "h-10 px-4 text-sm gap-2"
}

export function Button({
	as: Tag = "button",
	variant = "secondary",
	size = "md",
	icon: Icon,
	iconRight: IconRight,
	busy = false,
	className,
	children,
	...rest
}) {
	return (
		<Tag
			className={cx(
				"inline-flex select-none items-center justify-center whitespace-nowrap rounded-lg font-medium transition-colors",
				"disabled:cursor-not-allowed",
				BUTTON_VARIANTS[variant],
				BUTTON_SIZES[size],
				className
			)}
			{...rest}
		>
			{busy ? (
				<Loader2 size={15} className="animate-spin" aria-hidden />
			) : (
				Icon && <Icon size={15} aria-hidden />
			)}
			{children}
			{IconRight && <IconRight size={15} aria-hidden />}
		</Tag>
	)
}

export function IconButton({ label, icon: Icon, className, size = 16, ...rest }) {
	return (
		<button
			type="button"
			aria-label={label}
			title={label}
			className={cx(
				"inline-flex h-8 w-8 items-center justify-center rounded-lg text-ink-500",
				"transition-colors hover:bg-ink-100 hover:text-ink-900 disabled:text-ink-300",
				className
			)}
			{...rest}
		>
			<Icon size={size} aria-hidden />
		</button>
	)
}

/* -------------------------------------------------------------------------- */
/* Surfaces                                                                    */
/* -------------------------------------------------------------------------- */

export function Card({ className, children, ...rest }) {
	return (
		<div
			className={cx(
				"rounded-card border border-ink-200/80 bg-white shadow-card",
				className
			)}
			{...rest}
		>
			{children}
		</div>
	)
}

export function SectionHeading({ title, description, action, className }) {
	return (
		<div className={cx("flex items-end justify-between gap-4", className)}>
			<div className="min-w-0">
				<h2 className="text-[0.9375rem] font-semibold text-ink-900">{title}</h2>
				{description && (
					<p className="mt-0.5 text-[0.8125rem] leading-snug text-ink-500">
						{description}
					</p>
				)}
			</div>
			{action}
		</div>
	)
}

export function EmptyState({ icon: Icon, title, description, action, className }) {
	return (
		<div
			className={cx(
				"flex flex-col items-center justify-center gap-3 rounded-card",
				"border border-dashed border-ink-200 bg-white/60 px-6 py-12 text-center",
				className
			)}
		>
			{Icon && (
				<span className="grid h-10 w-10 place-items-center rounded-full bg-ink-100 text-ink-400">
					<Icon size={18} aria-hidden />
				</span>
			)}
			<div>
				<p className="text-sm font-semibold text-ink-800">{title}</p>
				{description && (
					<p className="mx-auto mt-1 max-w-sm text-[0.8125rem] leading-relaxed text-ink-500">
						{description}
					</p>
				)}
			</div>
			{action}
		</div>
	)
}

/**
 * Waiting for a page.
 *
 * The product's own mark rather than a generic ring: on a cold load this is
 * the first thing anyone sees, and a spinner that looks like everything else
 * makes the wait feel like nothing in particular is happening.
 */
export function Spinner({ label = "Loading", className }) {
	return (
		<div className={cx("flex items-center justify-center py-12", className)}>
			<BrandSpinner size={36} label={label} />
		</div>
	)
}

/* -------------------------------------------------------------------------- */
/* Badges                                                                      */
/* -------------------------------------------------------------------------- */

export function StatusBadge({ status, size = "md" }) {
	const isOpen = status === "open"
	return (
		<span
			className={cx(
				"inline-flex shrink-0 items-center gap-1.5 rounded-full font-semibold ring-1 ring-inset",
				size === "sm" ? "px-1.5 py-0.5 text-2xs" : "px-2 py-0.5 text-xs",
				isOpen
					? "bg-state-open-soft text-state-open-ink ring-state-open/25"
					: "bg-state-closed-soft text-state-closed-ink ring-state-closed/25"
			)}
		>
			{/* Never colour alone: the shape and the word both carry the state. */}
			{isOpen ? (
				<span
					className="h-1.5 w-1.5 rounded-full bg-state-open"
					aria-hidden
				/>
			) : (
				<Check size={11} strokeWidth={3} aria-hidden />
			)}
			{isOpen ? "Open" : "Resolved"}
		</span>
	)
}

export function TagChip({ tag, onRemove, size = "md", className }) {
	if (!tag) return null
	const tone = accent(tag.colour)
	return (
		<span
			className={cx(
				"inline-flex max-w-[12rem] items-center gap-1.5 rounded-full font-medium ring-1 ring-inset",
				size === "sm" ? "px-1.5 py-0.5 text-2xs" : "px-2 py-0.5 text-xs",
				tone.chip,
				className
			)}
		>
			<span className={cx("h-1.5 w-1.5 shrink-0 rounded-full", tone.dot)} aria-hidden />
			<span className="truncate">{tag.name}</span>
			{onRemove && (
				<button
					type="button"
					onClick={onRemove}
					className="-mr-0.5 rounded-full p-0.5 opacity-60 transition hover:bg-black/10 hover:opacity-100"
					aria-label={`Remove tag ${tag.name}`}
				>
					<X size={10} strokeWidth={3} aria-hidden />
				</button>
			)}
		</span>
	)
}

export function Avatar({ person, size = "md", className }) {
	const tone = accent(person?.colour)
	const initials =
		person?.initials ||
		(person?.name || "?")
			.split(" ")
			.map((part) => part[0])
			.slice(0, 2)
			.join("")
			.toUpperCase()

	const sizes = {
		xs: "h-5 w-5 text-[0.5625rem]",
		sm: "h-6 w-6 text-2xs",
		md: "h-8 w-8 text-xs",
		lg: "h-10 w-10 text-sm"
	}

	return (
		<span
			className={cx(
				"inline-grid shrink-0 place-items-center rounded-full font-semibold",
				sizes[size],
				tone.avatar,
				className
			)}
			title={person?.name}
		>
			{initials}
		</span>
	)
}

/* -------------------------------------------------------------------------- */
/* Form controls                                                               */
/* -------------------------------------------------------------------------- */

const FIELD =
	"rounded-lg border border-ink-200 bg-white px-3 text-sm text-ink-900 " +
	"placeholder:text-ink-400 transition-colors focus:border-brand-500 focus:outline-none " +
	"focus:ring-2 focus:ring-brand-500/25 disabled:bg-ink-50 disabled:text-ink-400"

/**
 * Fields fill their container unless the caller sets a width. Tailwind resolves
 * conflicting utilities by stylesheet order rather than by which one was passed
 * last, so `w-full` has to be omitted rather than overridden.
 */
function width(className) {
	return /(^|\s)(w-|max-w-|min-w-|flex-1)/.test(className || "") ? "" : "w-full"
}

export function Input({ className, ...rest }) {
	return <input className={cx(FIELD, "h-9", width(className), className)} {...rest} />
}

export function Textarea({ className, ...rest }) {
	return <textarea className={cx(FIELD, "py-2", width(className), className)} {...rest} />
}

export function Select({ className, children, ...rest }) {
	return (
		<select className={cx(FIELD, "h-9 pr-8", width(className), className)} {...rest}>
			{children}
		</select>
	)
}

/**
 * One choice from a few, all visible at once. Options are `{ value, label }`.
 */
export function ToggleGroup({ value, onChange, options, className, ...rest }) {
	return (
		<div
			role="radiogroup"
			className={cx(
				"inline-flex h-9 items-center gap-0.5 rounded-lg border border-ink-200 bg-white p-0.5",
				className
			)}
			{...rest}
		>
			{options.map((option) => {
				const selected = option.value === value
				return (
					<button
						key={option.value}
						type="button"
						role="radio"
						aria-checked={selected}
						onClick={() => onChange(option.value)}
						className={cx(
							"inline-flex h-full items-center rounded-md px-3 text-[0.8125rem] font-medium transition-colors",
							"focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/25",
							selected
								? "bg-brand-700 text-white shadow-card"
								: "bg-white text-ink-600 hover:bg-ink-50 hover:text-ink-900"
						)}
					>
						{option.label}
					</button>
				)
			})}
		</div>
	)
}

export function Field({ label, hint, error, children, className }) {
	return (
		<label className={cx("block", className)}>
			<span className="mb-1.5 block text-[0.8125rem] font-medium text-ink-700">
				{label}
			</span>
			{children}
			{error ? (
				<span className="mt-1 block text-xs text-rose-600">{error}</span>
			) : (
				hint && <span className="mt-1 block text-xs text-ink-500">{hint}</span>
			)}
		</label>
	)
}

export function ColourPicker({ value, onChange, options }) {
	return (
		<div className="flex flex-wrap gap-1.5">
			{options.map((key) => {
				const tone = accent(key)
				const selected = key === value
				return (
					<button
						key={key}
						type="button"
						onClick={() => onChange(key)}
						aria-label={tone.label}
						aria-pressed={selected}
						title={tone.label}
						className={cx(
							"h-7 w-7 rounded-full transition",
							tone.swatch,
							selected
								? "ring-2 ring-ink-900 ring-offset-2"
								: "ring-1 ring-black/10 hover:scale-110"
						)}
					/>
				)
			})}
		</div>
	)
}

/* -------------------------------------------------------------------------- */
/* Overlays                                                                    */
/* -------------------------------------------------------------------------- */

export function Modal({ open, onClose, title, description, children, footer, size = "md" }) {
	const panelRef = useRef(null)

	useEffect(() => {
		if (!open) return undefined
		const onKey = (event) => {
			if (event.key === "Escape") onClose?.()
		}
		document.addEventListener("keydown", onKey)
		const previous = document.body.style.overflow
		document.body.style.overflow = "hidden"
		return () => {
			document.removeEventListener("keydown", onKey)
			document.body.style.overflow = previous
		}
	}, [open, onClose])

	if (!open) return null

	const widths = { sm: "max-w-md", md: "max-w-xl", lg: "max-w-3xl" }

	return createPortal(
		<div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-6">
			<div
				className="keep-light fixed inset-0 bg-ink-950/40 backdrop-blur-[2px]"
				onClick={onClose}
				aria-hidden
			/>
			<div
				ref={panelRef}
				role="dialog"
				aria-modal="true"
				aria-label={title}
				className={cx(
					"relative z-10 my-auto w-full animate-scale-in rounded-xl bg-white shadow-pop",
					widths[size]
				)}
			>
				<header className="flex items-start justify-between gap-4 border-b border-ink-100 px-5 py-4">
					<div className="min-w-0">
						<h2 className="text-base font-semibold text-ink-900">{title}</h2>
						{description && (
							<p className="mt-0.5 text-[0.8125rem] leading-snug text-ink-500">
								{description}
							</p>
						)}
					</div>
					<IconButton label="Close" icon={X} onClick={onClose} className="-mr-1.5 -mt-1" />
				</header>
				<div className="px-5 py-4">{children}</div>
				{footer && (
					<footer className="flex items-center justify-end gap-2 rounded-b-xl border-t border-ink-100 bg-ink-50/60 px-5 py-3">
						{footer}
					</footer>
				)}
			</div>
		</div>,
		document.body
	)
}

/** Click-outside dropdown container. */
/**
 * A panel anchored to its trigger.
 *
 * The panel is rendered through a portal at the document root and positioned
 * with fixed coordinates rather than being absolutely placed inside the
 * trigger. Anchoring it in the flow looks identical until the popover sits
 * inside a card that clips its overflow - a rounded card, a scrolling list -
 * at which point the panel is silently cut off and the control simply stops
 * working. Going through a portal removes the whole class of bug.
 *
 * It also means the panel can flip above the trigger when there is no room
 * below, which is the common case for a control near the bottom of a card.
 */
export function Popover({ trigger, children, align = "right", className }) {
	const [open, setOpen] = useState(false)
	const [rect, setRect] = useState(null)
	const hostRef = useRef(null)
	const panelRef = useRef(null)

	const place = useCallback(() => {
		const anchor = hostRef.current?.firstElementChild || hostRef.current
		if (anchor) setRect(anchor.getBoundingClientRect())
	}, [])

	useLayoutEffect(() => {
		if (open) place()
	}, [open, place])

	useEffect(() => {
		if (!open) return undefined
		const onDown = (event) => {
			if (hostRef.current?.contains(event.target)) return
			if (panelRef.current?.contains(event.target)) return
			setOpen(false)
		}
		const onKey = (event) => {
			if (event.key === "Escape") setOpen(false)
		}
		// Scroll anywhere - the page, a list the trigger sits in - moves the
		// anchor, so the panel is repositioned rather than left behind.
		document.addEventListener("mousedown", onDown)
		document.addEventListener("keydown", onKey)
		window.addEventListener("scroll", place, true)
		window.addEventListener("resize", place)
		return () => {
			document.removeEventListener("mousedown", onDown)
			document.removeEventListener("keydown", onKey)
			window.removeEventListener("scroll", place, true)
			window.removeEventListener("resize", place)
		}
	}, [open, place])

	const style = {}
	if (rect) {
		const margin = 8
		const below = window.innerHeight - rect.bottom
		const flip = below < 240 && rect.top > below
		if (flip) style.bottom = `${window.innerHeight - rect.top + 6}px`
		else style.top = `${rect.bottom + 6}px`

		if (align === "right") {
			style.right = `${Math.max(margin, window.innerWidth - rect.right)}px`
		} else {
			style.left = `${Math.min(rect.left, window.innerWidth - margin)}px`
		}
		style.maxHeight = `${Math.max(180, (flip ? rect.top : below) - 16)}px`
	}

	return (
		<div ref={hostRef} className="relative">
			{trigger({ open, toggle: () => setOpen((v) => !v), close: () => setOpen(false) })}
			{open &&
				rect &&
				createPortal(
					<div
						ref={panelRef}
						style={style}
						className={cx(
							"fixed z-50 flex animate-fade-in flex-col overflow-y-auto rounded-xl",
							"border border-ink-200 bg-white p-1 shadow-pop scrollbar-slim",
							className
						)}
					>
						{children({ close: () => setOpen(false) })}
					</div>,
					document.body
				)}
		</div>
	)
}

/* -------------------------------------------------------------------------- */
/* Toasts                                                                      */
/* -------------------------------------------------------------------------- */

const TOAST_TONES = {
	info: { icon: Info, className: "text-brand-700" },
	success: { icon: Check, className: "text-state-closed" },
	error: { icon: AlertTriangle, className: "text-rose-600" }
}

export function ToastStack({ toasts, onDismiss }) {
	if (!toasts.length) return null
	return createPortal(
		<div className="pointer-events-none fixed bottom-4 left-1/2 z-[60] flex w-full max-w-md -translate-x-1/2 flex-col gap-2 px-4">
			{toasts.map((toast) => {
				const tone = TOAST_TONES[toast.tone] || TOAST_TONES.info
				const Icon = tone.icon
				return (
					<div
						key={toast.id}
						role="status"
						className="pointer-events-auto flex animate-fade-in items-start gap-2.5 rounded-xl border border-ink-200 bg-white px-3.5 py-2.5 shadow-pop"
					>
						<Icon size={16} className={cx("mt-0.5 shrink-0", tone.className)} aria-hidden />
						<p className="flex-1 text-[0.8125rem] leading-snug text-ink-800">
							{toast.message}
						</p>
						<IconButton
							label="Dismiss"
							icon={X}
							size={13}
							className="-mr-1 -mt-0.5 h-6 w-6"
							onClick={() => onDismiss(toast.id)}
						/>
					</div>
				)
			})}
		</div>,
		document.body
	)
}
